import { useEffect, useRef, useState } from "react";
import "../Common/css/styles.css";
import { chatApi, chatSignalR } from "src/Services/chat_api";
import { communitySignalR } from "src/Services/community_chat_api";
import env from "src/env";
import { getComMessages, sendComMessage, markCommunityMessagesAsRead } from "src/Services/com_chat_api";
import avatar from "src/Assets/Images/avatar.png";
import { getSpg } from "src/Services/auth_api";
import soldier from "src/Assets/Images/soldier.png";
import knight from "src/Assets/Images/knight.png";
import emperor from "src/Assets/Images/emperor.png";
import { Divider } from "@mui/material";

export default function AdminChats() {
    const [selectedChat, setSelectedChat] = useState<any>(null);
    const [activeChatId, setActiveChatId] = useState<number | string | null>(null);
    const [isMobileChatOpen, setIsMobileChatOpen] = useState(false);
    const [conversations, setConversations] = useState<any[]>([]);
    const [messages, setMessages] = useState<any[]>([]);
    const [viewAttachment, setViewAttachment] = useState<any>(null);
    const [message, setMessage] = useState<string>("");
    const [attachments, setAttachments] = useState<File[]>([]);
    const [localConversationId, setLocalConversationId] = useState<number | null>(null);
    const [isTyping, setIsTyping] = useState<boolean>(false);
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const [isCommunityConnected, setIsCommunityConnected] = useState<boolean>(false);
    const [labelId, setLabelId] = useState("")
    const [communityTypingUsers, setCommunityTypingUsers] = useState<Set<string>>(new Set());
    const [onlineUsers, setOnlineUsers] = useState<any[]>([]);
    const [spg, setSpg] = useState<any>();
    const [spgModalOpen, setSpgModalOpen] = useState(false);
    const [profileUser, setProfileUser] = useState<{ id: any; name?: string; type?: any } | null>(null);
    const [spgLoading, setSpgLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const communityTypingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const localConversationIdRef = useRef<number | null>(null);
    const isFetchingSpgRef = useRef(false);

    const labels = [
        { id: '1', name: 'Study Tip' },
        {id: '2', name: 'Institute Announcement'},
        {id: '3', name: 'Badge Specific'}
    ]

    // Keep ref in sync with state
    useEffect(() => {
        localConversationIdRef.current = localConversationId;
    }, [localConversationId]);

    const handleAttachmentClick = (file: any) => {
        const fileUrl = `${BASE_URL}${file.filePath}`;
        const isImage = file.fileType.startsWith("image/");
        const isPDF = file.fileType === "application/pdf";

        setViewAttachment({
            fileUrl,
            fileName: file.fileName,
            type: isImage ? "image" : "pdf",
        });
    };

    const userId = sessionStorage.getItem("adminId") || "";
    const BASE_URL = env.BASE_URL;

    const messagesEndRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // Connect to SignalR on component mount
    useEffect(() => {
        chatSignalR.connect()
            .then(() => {
                setIsConnected(true);
            })
            .catch(err => {
                setIsConnected(false);
            });

        return () => {
            chatSignalR.disconnect().catch(console.error);
            setIsConnected(false);
        };
    }, []);

    // Connect to Community SignalR
    useEffect(() => {
        communitySignalR.connect()
            .then(() => {
                setIsCommunityConnected(true);
                return communitySignalR.joinCommunity();
            })
            .then(() => {
            })
            .catch(err => {
                setIsCommunityConnected(false);
            });

        return () => {
            communitySignalR.leaveCommunity().catch(console.error);
            communitySignalR.disconnect().catch(console.error);
            setIsCommunityConnected(false);
        };
    }, []);

    // Polling for new messages (temporary workaround until backend broadcasts via SignalR)
    useEffect(() => {
        if (!localConversationId) return;

        const pollMessages = async () => {
            try {
                const res = await chatApi.getMessages(localConversationId, Number(userId), 2, 50);
                setMessages(prev => {
                    // Only update if there are new messages
                    if (res.length > prev.length) {
                        return res;
                    }
                    // Check if last message is different
                    if (res.length > 0 && prev.length > 0) {
                        const lastNew = res[res.length - 1];
                        const lastOld = prev[prev.length - 1];
                        if (lastNew.id !== lastOld.id) {
                            return res;
                        }
                    }
                    return prev;
                });
            } catch (error) {
                console.error('Polling error:', error);
            }
        };

        // Poll every 3 seconds
        const intervalId = setInterval(pollMessages, 3000);

        return () => clearInterval(intervalId);
    }, [localConversationId, userId]);

    // Subscribe to SignalR events - use a single useEffect with stable handlers
    useEffect(() => {
        if (!isConnected) return;

        // Handle incoming real-time messages
        const handleReceiveMessage = (data: any) => {

            // Update messages if it belongs to current conversation
            if (data.conversationId === localConversationIdRef.current) {
                setMessages(prev => {
                    const exists = prev.some(m => m.id === data.id);
                    if (exists) return prev;
                    return [...prev, data];
                });

                // Mark as read if the message is from the other party (student)
                if (data.senderType !== 2 && localConversationIdRef.current) {
                    chatApi.markAsRead(localConversationIdRef.current, Number(userId), 2).catch(console.error);
                }
            }

            // Update conversation list with new message
            setConversations(prev => prev.map(conv => {
                if (conv.id === data.conversationId) {
                    return {
                        ...conv,
                        lastMessage: data.content,
                        unreadCount: conv.id === localConversationIdRef.current ? 0 : conv.unreadCount + 1
                    };
                }
                return conv;
            }));
        };

        // Handle typing indicator
        const handleUserTyping = (data: any) => {
            if (data.conversationId === localConversationIdRef.current && data.senderType !== 2) {
                setIsTyping(true);
            }
        };

        const handleStopTyping = (data: any) => {
            if (data.conversationId === localConversationIdRef.current) {
                setIsTyping(false);
            }
        };

        chatSignalR.on('ReceiveMessage', handleReceiveMessage);
        chatSignalR.on('UserTyping', handleUserTyping);
        chatSignalR.on('StopTyping', handleStopTyping);

        return () => {
            chatSignalR.off('ReceiveMessage', handleReceiveMessage);
            chatSignalR.off('UserTyping', handleUserTyping);
            chatSignalR.off('StopTyping', handleStopTyping);
        };
    }, [isConnected]); // Only re-subscribe when connection status changes

    // Subscribe to Community Chat SignalR events
    useEffect(() => {
        if (!isCommunityConnected) return;

        const handleReceiveCommunityMessage = (data: any) => {
            // Only add message if we're currently viewing community chat
            if (selectedChat?.id === "community") {
                setMessages(prev => [...prev, data]);
            }
        };

        const handleUserTypingCommunity = (data: any) => {
            if (selectedChat?.id === "community") {
                setCommunityTypingUsers(prev => new Set(prev).add(`${data.userId}`));
            }
        };

        const handleUserStoppedTypingCommunity = (data: any) => {
            if (selectedChat?.id === "community") {
                setCommunityTypingUsers(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(`${data.userId}`);
                    return newSet;
                });
            }
        };

        const handleOnlineUsersUpdated = (users: any[]) => {
            setOnlineUsers(users);
        };

        const handleMessageDeleted = (messageId: number) => {
            // Mark message as deleted in the UI
            setMessages(prev => prev.map(msg => 
                msg.id === messageId ? { ...msg, isDeleted: true } : msg
            ));
        };

        communitySignalR.on('ReceiveCommunityMessage', handleReceiveCommunityMessage);
        communitySignalR.on('UserTyping', handleUserTypingCommunity);
        communitySignalR.on('UserStoppedTyping', handleUserStoppedTypingCommunity);
        communitySignalR.on('OnlineUsersUpdated', handleOnlineUsersUpdated);
        communitySignalR.on('MessageDeleted', handleMessageDeleted);

        return () => {
            communitySignalR.off('ReceiveCommunityMessage', handleReceiveCommunityMessage);
            communitySignalR.off('UserTyping', handleUserTypingCommunity);
            communitySignalR.off('UserStoppedTyping', handleUserStoppedTypingCommunity);
            communitySignalR.off('OnlineUsersUpdated', handleOnlineUsersUpdated);
            communitySignalR.off('MessageDeleted', handleMessageDeleted);
        };
    }, [isCommunityConnected, selectedChat?.id]);

    // Handle typing indicator - send to SignalR
    const handleTyping = () => {
        // Handle regular chat typing
        if (localConversationId && isConnected) {
            chatSignalR.userTyping(Number(userId), localConversationId, 'Instructor').catch(console.error);

            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }

            typingTimeoutRef.current = setTimeout(() => {
                if (localConversationId) {
                    chatSignalR.stopTyping(Number(userId), localConversationId).catch(console.error);
                }
            }, 2000);
        }

        // Handle community chat typing
        if (selectedChat?.id === "community" && isCommunityConnected) {
            communitySignalR.startTyping().catch(console.error);

            if (communityTypingTimeoutRef.current) {
                clearTimeout(communityTypingTimeoutRef.current);
            }

            communityTypingTimeoutRef.current = setTimeout(() => {
                communitySignalR.stopTyping().catch(console.error);
            }, 2000);
        }
    };


    const communityChat = {
        id: "community",
        name: "Community Chat",
        lastMessage: "Welcome to our community!",
        unreadCount: 0
    };

    const handleSendMessageInCommunity = async () => {
        if (isSending) return;
        setIsSending(true);
        const formdata = new FormData();
        formdata.append("SenderId", userId)
        formdata.append("SenderType", "3")
        formdata.append("Content", message)
        formdata.append("MessageTag", labelId)
        formdata.append("Attachment", attachments.length > 0 ? attachments[0] : "");

        try {
            await sendComMessage(sessionStorage.getItem("token") || "", formdata);
            setMessage("");
            setAttachments([]);
            setLabelId("")
            
            // Wait a bit for backend to process the message
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Fetch and display updated messages, then mark as read
            try {
                const token = sessionStorage.getItem("token") || "";
                const res = await getComMessages(token, userId, "3");
                const communityMessages = res.data.messages || [];
                setMessages(communityMessages);
                
                const lastMessageId = communityMessages.length > 0 
                    ? communityMessages[communityMessages.length - 1].id 
                    : 0;
                await markCommunityMessagesAsRead(token, userId, 3, lastMessageId);
                window.dispatchEvent(new Event('communityMessagesRead'));
            } catch (error) {
                console.error("Error marking messages as read:", error);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsSending(false);
        }
    }

    const handleGetCommunityMessages = async () => {
        try {
            const res = await getComMessages(sessionStorage.getItem("token") || "", userId, "3");
            setMessages(res.data.messages);
        } catch (error) {
            setMessages([]);
        }
    }

    useEffect(() => {
        openChat(communityChat);
        handleGetCommunityMessages();
    }, []);

    const openChat = async (chat: any) => {
        setSelectedChat(chat);
        setActiveChatId(chat.id);

        if (chat.id !== "community") {
            setLocalConversationId(chat.id);
            handleGetMessages(chat.id);
        } else {
            setLocalConversationId(null);
            // Load community messages first
            try {
                const token = sessionStorage.getItem("token") || "";
                const res = await getComMessages(token, userId, "3");
                const communityMessages = res.data.messages || [];
                setMessages(communityMessages);
                
                // Get the last message ID and mark as read
                const lastMessageId = communityMessages.length > 0 
                    ? communityMessages[communityMessages.length - 1].id 
                    : 0;
                
                await markCommunityMessagesAsRead(token, userId, 3, lastMessageId);
                
                // Trigger navbar badge count update
                window.dispatchEvent(new Event('communityMessagesRead'));
            } catch (error) {
                console.error("Error loading community messages:", error);
                setMessages([]);
            }
        }

        if (window.innerWidth <= 426) {
            setIsMobileChatOpen(true);
        }
    };

    const handleAttach = (e: any) => {
        if (!e.target.files) return;
        const file = e.target.files[0];

        if (!["image/jpeg", "image/png", "image/jpg", "application/pdf"].includes(file.type)) {
            alert("Only images or PDF files allowed.");
            e.target.value = "";
            return;
        }

        if (attachments.length >= 1) {
            alert("Only 1 attachment allowed.");
            e.target.value = "";
            return;
        }

        setAttachments([file]);
        e.target.value = "";
    };

    const handleSend = async () => {
        if (isSending) return;
        if (message.trim() === "" && attachments.length === 0) return;

        // Stop typing indicator
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }
        if (communityTypingTimeoutRef.current) {
            clearTimeout(communityTypingTimeoutRef.current);
        }
        
        if (localConversationId && isConnected) {
            chatSignalR.stopTyping(Number(userId), localConversationId).catch(console.error);
        }
        if (selectedChat?.id === "community" && isCommunityConnected) {
            communitySignalR.stopTyping().catch(console.error);
        }

        // COMMUNITY CHAT - using REST API with SignalR updates
        if (selectedChat?.id === "community") {
            try {
                setIsSending(true);
                await handleSendMessageInCommunity();
            } finally {
                setIsSending(false);
            }
            return;
        }

        // NORMAL MESSAGE
        try {
            setIsSending(true);
            const sentMessage = await chatApi.sendMessage({
                conversationId: localConversationId,
                senderId: Number(userId),
                senderType: 2,
                content: message,
                attachment: attachments.length > 0 ? attachments[0] : null,
            });

            const messageContent = message; // Store before clearing
            setMessage("");
            setAttachments([]);
            setViewAttachment(null);

            // Create the message object for display
            const messageObj = sentMessage?.id ? {
                ...sentMessage,
                createdAt: sentMessage.createdAt || new Date().toISOString()
            } : {
                id: Date.now(),
                conversationId: localConversationId,
                senderId: Number(userId),
                senderType: 2,
                content: messageContent,
                createdAt: new Date().toISOString(),
                attachments: []
            };

            // Add sent message to local state
            setMessages(prev => {
                const exists = prev.some(m => m.id === messageObj.id);
                if (exists) return prev;
                return [...prev, messageObj];
            });

            // Note: Backend should broadcast ReceiveMessage after saving
            // Until then, polling will fetch new messages for the receiver
        } catch (err) {
            console.error("Send message error:", err);
        } finally {
            setIsSending(false);
        }
    };


    const goBackMobile = () => {
        setIsMobileChatOpen(false);
        setSelectedChat(null);
        setActiveChatId(null);
    };

    const handleReadMessages = async (conversationId: number) => {
        try {
            await chatApi.markAsRead(conversationId, Number(userId), 2);
        } catch (error) {
            console.error("Error marking messages as read:", error);
        }
    };

    const handleGetMessages = async (conversationId: number) => {

        setMessages([]);

        try {
            const res = await chatApi.getMessages(conversationId, Number(userId), 2, 50);
            setMessages(res);
            // Mark messages as read when loading conversation
            chatApi.markAsRead(conversationId, Number(userId), 2).catch(console.error);
        } catch (error) {
            setMessages([]);
        }
    }

    // Fetch SPG details
    const handleGetSpg = async (userId: any, userType: any, userName?: string) => {
        if (isFetchingSpgRef.current) return;
        isFetchingSpgRef.current = true;

        setProfileUser({ id: userId, name: userName, type: userType });
        setSpg(null);
        setSpgModalOpen(true);
        setSpgLoading(true);

        if (Number(userType) === 1) {
            try {
                const res = await getSpg(userId, sessionStorage.getItem("token") || "");
                setSpg(res.data || null);
            } catch {
                setSpg(null);
            } finally {
                setSpgLoading(false);
                isFetchingSpgRef.current = false;
            }
        } else {
            setSpgLoading(false);
            isFetchingSpgRef.current = false;
        }
    }

    return (
        <div className="chats-container-dark">

            {/* LEFT LIST */}
            <div
                className={`chats-list-panel-dark ${isMobileChatOpen ? "chats-list-hidden-mobile" : ""
                    }`}
            >
                <h3 className="chats-list-title-dark">Chats</h3>

                {/* COMMUNITY CHAT */}
                <div
                    className={`chats-item-dark chats-community-dark ${activeChatId === "community" ? "chats-item-active-dark" : ""
                        }`}
                    onClick={() => {
                        setMessages([]);
                        setAttachments([]);
                        setMessage("");
                        openChat(communityChat)
                        handleGetCommunityMessages();
                    }}
                >
                    <div className="chats-item-info-dark">
                        <div className="chats-item-name-dark">{communityChat.name}</div>
                    </div>
                </div>

                <div className="chats-divider-dark"></div>

                {/* API CHAT LIST */}
                {conversations.map((chat) => (
                    <div
                        key={chat.id}
                        className={`chats-item-dark ${activeChatId === chat.id ? "chats-item-active-dark" : ""
                            }`}
                        onClick={() => {
                            openChat(chat);
                            handleGetMessages(chat.id);
                            handleReadMessages(chat.id);
                            setConversations(prev =>
                                prev.map(c =>
                                    c.id === chat.id ? { ...c, unreadCount: 0 } : c
                                )
                            );
                            setAttachments([]);
                            setMessage("");
                        }}
                    >
                        <div className="chats-item-info-dark">
                            <div className="chats-item-name-dark">{chat.name}</div>
                            <div className="chats-item-last-dark">
                                {chat.lastMessage || "Messages."}
                            </div>
                        </div>

                        {/* UNREAD BADGE */}
                        {chat.unreadCount > 0 && (
                            <div className="chats-unread-badge-dark">
                                {chat.unreadCount}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* RIGHT CHAT PANEL */}
            <div
                className={`chats-chat-panel-dark ${isMobileChatOpen ? "chats-chat-mobile-open" : ""
                    }`}
            >
                {selectedChat ? (
                    <>
                        {isMobileChatOpen && (
                            <button className="chats-back-btn-dark" onClick={goBackMobile}>
                                ← Back
                            </button>
                        )}

                        <div className="chats-chat-header-dark">
                            <h3 className="chats-chat-title-dark">{selectedChat.name}</h3>
                        </div>

                        <div className="chats-chat-body-dark">
                            {messages.length === 0 && (
                                <div className="chats-empty-dark">Messages.</div>
                            )}

                            {messages.map((msg, index) => {
                                const isOutgoing = msg.senderType === 3 && msg.senderId === Number(userId);

                                return (
                                    <div
                                        key={index}
                                        className={`chat-message ${isOutgoing ? "outgoing" : "incoming"}`}
                                    >
                                        {selectedChat.id === "community" && msg.messageTag && (
                                            <div className={`chat-message-label ${isOutgoing ? 'outgoing-label' : 'incoming-label'}`}>
                                                {msg.messageTag === 1 ? "Study Tip" : msg.messageTag === 2 ? "Institution Announcements" : msg.messageTag === 3 ? "Badge Specific" : ""}
                                            </div>
                                        )}

                                        {selectedChat.id === "community" && (
                                            <div>
                                                <div className={`chat-sender-name ${isOutgoing ? "outgoing-name" : "incoming-name"}`}>{msg.senderName}</div>
                                                <img
                                                    className={`chat-avatar ${isOutgoing ? "outgoing-avatar" : "incoming-avatar"}`}
                                                    onClick={() => handleGetSpg(msg.senderId, msg.senderType, msg.senderName)}
                                                    src={avatar}
                                                    alt="User Avatar"
                                                    style={{ cursor: "pointer" }}
                                                />
                                            </div>
                                        )}
                                        {/* TEXT MESSAGE */}
                                        {msg.content && <span>{msg.content}</span>}

                                        {/* ATTACHMENTS */}
                                        {msg.attachments && msg.attachments.length > 0 && (
                                            <div className="chat-attachment-container">
                                                {msg.attachments.map((file: any, idx: number) => {
                                                    const fileUrl = `${BASE_URL}${file.filePath}`;
                                                    const isImage = file.fileType.startsWith("image/");
                                                    const isPDF = file.fileType === "application/pdf";

                                                    return (
                                                        <div
                                                            key={idx}
                                                            className="chat-attachment-item"
                                                            onClick={() => handleAttachmentClick(file)}
                                                        >
                                                            {isImage && (
                                                                <img
                                                                    src={fileUrl}
                                                                    className="chat-img-thumb"
                                                                    alt={file.fileName}
                                                                />
                                                            )}

                                                            {isPDF && (
                                                                <div className="chat-pdf-thumb">
                                                                    <p>{file.fileName}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* TIME */}
                                        <div className="chat-time">
                                            {msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString([], {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            }) : ''}
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Typing Indicator */}
                            {isTyping && selectedChat?.id !== "community" && (
                                <div className="chat-message incoming">
                                    <span style={{ fontStyle: 'italic', color: '#888' }}>
                                        {selectedChat?.name} is typing...
                                    </span>
                                </div>
                            )}

                            {selectedChat?.id === "community" && communityTypingUsers.size > 0 && (
                                <div className="chat-message incoming">
                                    <span style={{ fontStyle: 'italic', color: '#888' }}>
                                        User{communityTypingUsers.size > 1 ? 's' : ''} typing...
                                    </span>
                                </div>
                            )}

                            <div ref={messagesEndRef}></div>
                        </div>
                        {attachments.length > 0 && (
                            <div className="chat-attachments">
                                {attachments.map((file, idx) => {
                                    const isImage = file.type.startsWith("image/");
                                    const isPDF = file.type === "application/pdf";

                                    const fileUrl = URL.createObjectURL(file); // preview URL

                                    return (
                                        <div className="chat-attachment-preview" key={idx}>
                                            {/* Preview */}
                                            {isImage && (
                                                <img
                                                    src={fileUrl}
                                                    className="chat-img-thumb"
                                                    onClick={() =>
                                                        setViewAttachment({ fileUrl, type: "image", fileName: file.name })
                                                    }
                                                    style={{ cursor: "pointer" }}
                                                />
                                            )}

                                            {isPDF && (
                                                <div
                                                    className="chat-pdf-thumb"
                                                    onClick={() =>
                                                        setViewAttachment({ fileUrl, type: "pdf", fileName: file.name })
                                                    }
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <p>{file.name}</p>
                                                </div>
                                            )}

                                            {/* REMOVE BUTTON */}
                                            <button
                                                className="chat-attachment-remove-btn"
                                                onClick={() => setAttachments([])}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <div className="chats-chat-input-dark">

                            {/* File Picker */}
                            <input
                                accept="image/*,.pdf"
                                id="attach-file"
                                type="file"
                                style={{ display: "none" }}
                                onChange={handleAttach}
                            />
                            <label htmlFor="attach-file" className="chats-attach-btn-dark" style={{ cursor: "pointer" }}>📎</label>

                            {/* Message Box */}
                            <input
                                type="text"
                                placeholder="Type a message..."
                                className="chats-input-box-dark"
                                value={message}
                                onChange={(e) => {
                                    setMessage(e.target.value);
                                    handleTyping();
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend();
                                    }
                                }}
                            />

                            <button className="chats-send-btn-dark" onClick={handleSend}>Send</button>
                        </div>
                        {selectedChat?.id === "community" && (
                            <div className="label-outer">
                                {labels.map((label) => (
                                    <button
                                        key={label.id}
                                        className={`label-badge ${labelId === label.id ? "active" : ""}`}
                                        onClick={() =>
                                            setLabelId(prev => prev === label.id ? "" : label.id)
                                        }
                                        aria-pressed={labelId === label.id}
                                    >
                                        {label.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    <div className="chats-empty-dark">Select a chat to start messaging.</div>
                )}
            </div>

            {viewAttachment && (
                <div className="chat-view-attachment-overlay" onClick={() => setViewAttachment(null)}>
                    <div className="chat-view-attachment-box" onClick={e => e.stopPropagation()}>
                        {/* Close */}
                        <button className="chat-view-close-btn" onClick={() => setViewAttachment(null)}>
                            ✕
                        </button>

                        {viewAttachment.type === "image" && (
                            <img src={viewAttachment.fileUrl} className="chat-view-image" />
                        )}

                        {viewAttachment.type === "pdf" && (
                            <iframe src={viewAttachment.fileUrl} className="chat-view-pdf"></iframe>
                        )}
                    </div>
                </div>
            )}

            {spgModalOpen && (
                <div className="spg-modal-overlay" onClick={() => setSpgModalOpen(false)}>
                    <div className="spg-modal-box" onClick={e => e.stopPropagation()}>
                        <button className="spg-modal-close" onClick={() => setSpgModalOpen(false)}>✕</button>

                        <div className="spg-modal-content">
                            <div className="spg-modal-header">
                                <h3 style={{ margin: 0 }}>{profileUser?.name ?? "User"}</h3>
                                <div style={{ fontSize: 12, color: "#aaa" }}>
                                    {profileUser?.type === 1 ? "Student" : profileUser?.type === 3 ? "Admin" : "Teacher"}
                                </div>
                            </div>

                            <Divider style={{ borderColor: "#555", margin: "12px 0" }} />

                            <div className="spg-modal-body">
                                {spgLoading ? (
                                    <div style={{ textAlign: "center", padding: 18 }}>Loading...</div>
                                ) : (
                                    <>
                                        {spg ? (
                                            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                                                {(() => {
                                                    const _badgeKey = String(spg.badge || "").trim().toLowerCase();
                                                    const badgeImg = (_badgeKey === "rookie" || _badgeKey === "soldier")
                                                        ? soldier
                                                        : (_badgeKey === "knight")
                                                            ? knight
                                                            : emperor;
                                                    return (
                                                        <img src={badgeImg} alt={spg.badge || "Badge"} style={{ width: 84, height: 84, objectFit: "contain" }} />
                                                    );
                                                })()}

                                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                    <div style={{ fontSize: 13, color: "#aaa" }}>SPG</div>
                                                    <div style={{ fontSize: 28, color: "#d3aa04", fontWeight: 800 }}>{(parseFloat(String(spg.gpa || spg.gpaScore || "0")) || 0).toFixed(2)}</div>
                                                    <div style={{ fontSize: 13, color: "#fff" }}><strong style={{ color: "#d9ae03" }}>Badge:</strong> {spg.badge === "Rookie" ? "Soldier" : spg.badge === "Soldier" ? "Soldier" : spg.badge === "Knight" ? "Knight" : "Emperor"}</div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{ padding: 8, color: "#ccc" }}>
                                                No SPG details available for this user.
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
